/**
 * Fetch every row of a PostgREST query by looping `.range()` pages.
 *
 * Supabase/PostgREST silently caps un-limited selects at max-rows (1000 by
 * default), so a query without `.limit()`/`.range()` quietly drops rows once a
 * table grows past the cap. Any "load the whole list" datasource method must go
 * through this helper instead of issuing a bare select.
 *
 * `buildPage` must return a fresh query for the given range (PostgREST
 * builders are single-use) and MUST apply a deterministic `.order()` —
 * otherwise pages can overlap or skip rows.
 */
export async function fetchAllPaged<T>(
  buildPage: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
  pageSize = 1000,
): Promise<T[]> {
  const all: T[] = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildPage(from, from + pageSize - 1);
    if (error) throw new Error(error.message);
    if (!data?.length) break;
    all.push(...data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}
